<template>
	<MobileCustomerPicker
		v-if="useMobilePicker"
		v-bind="$attrs"
		:items="customers"
		:loading="isLoading"
		:search="searchTerm"
		:model-value="selected"
		:disabled="isDisabled"
		:show-create="shouldShowCreateButton"
		:item-text="getAutocompleteItemText"
		@update:search="searchTerm = $event"
		@create="handleCreateClick"
		@open="refresh" />
	<BaseAutoComplete
		v-else
		v-bind="{ ...$attrs, ...searchFieldProps(searchTerm, ($attrs.errorMessages ?? $attrs['error-messages'] ?? []) as string | string[]) }"
		ref="customerAutocompleteRef"
		:items="customers"
		:loading="isLoading"
		:item-title="getAutocompleteItemText"
		:menu-props="menuProps"
		no-data-text="No contacts found."
		v-model:search="searchTerm"
		:chip-text="getChipText"
		:chip-editable="true"
		clearable
		@focus="() => items.length === 0 && refresh()"
		@edit-chip="handleChipEdit">
		<!-- TODO: move to base class.-->
		<template #no-data>
			<v-list-item class="pl-2">
				<v-list-item-title>{{ isLoading ? "Loading contacts..." : "No contacts found." }}</v-list-item-title>
			</v-list-item>
		</template>

		<template v-if="shouldShowCreateButton" #append-item>
			<v-list-item class="create-contact-fixed pa-0" @mousedown.prevent @click.stop="handleCreateClick">
				<div class="text-center pt-0 pb-2 w-100">
					<hr class="mb-2" />
					<v-btn
						class="elevation-0 pa-2 text-none create-contact-btn create-action-btn"
						color="transparent"
						:ripple="false"
						style="pointer-events: none">
						Create contact '<span class="create-action-query">{{ searchTerm }}</span
						>'
					</v-btn>
				</div>
			</v-list-item>
		</template>

		<template #item="{ props, item }">
			<v-list-item v-bind="props" :title="getAutocompleteItemText(item.raw)">
				<v-list-item-subtitle v-if="shouldShowItemPhoneSubtitle(item.raw)">
					<span>{{ item.raw.phone }}</span>
				</v-list-item-subtitle>
				<v-list-item-subtitle v-if="shouldShowItemEmailSubtitle(item.raw)">
					<span>{{ item.raw.email }}</span>
				</v-list-item-subtitle>
			</v-list-item>
		</template>

		<template #selection="{ item }">
			<span class="text-truncate">{{ getCustomerDisplayName(item.raw) || "[Contact]" }}</span>
		</template>

		<template #append-inner>
			<!-- stop mousedown so the autocomplete menu doesn't toggle -->
			<span @mousedown.stop.prevent @click.stop="handleAppendClick">
				<v-tooltip v-if="!isDisabled" location="top" :text="tooltipText">
					<template #activator="{ props: tooltipProps }">
						<v-icon v-bind="tooltipProps" v-test="'customer-create-or-edit'" color="primary" class="cursor-pointer">{{
							appendIcon
						}}</v-icon>
					</template>
				</v-tooltip>
			</span>
		</template>
	</BaseAutoComplete>
</template>

<script setup lang="ts">
	import { searchFieldProps } from "@/helpers/searchLimit";
	import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
	import MobileCustomerPicker from "@/components/formfields/MobileCustomerPicker.vue";
	import { getSearchLabelForCustomers, getCustomerDisplayName } from "@/formatters/customers";
	import type { Customer } from "@/types/Customer";
	import api from "@/api";
	import { computed, getCurrentInstance, onMounted, onUpdated, ref, type Ref } from "vue";
	import { useDisplay } from "vuetify";
	import { useAsyncAutocompleteItems } from "@/composables/useAsyncAutocompleteItems";
	import { useKeyboardSafeMenu } from "@/composables/useKeyboardSafeMenu";

	defineOptions({ inheritAttrs: false });
	const props = withDefaults(defineProps<{ mobilePicker?: boolean }>(), { mobilePicker: false });
	const { smAndDown } = useDisplay();
	const useMobilePicker = computed(() => props.mobilePicker && smAndDown.value);

	type BaseAutoCompleteComponent = {
		getActivatorElement?: () => HTMLElement | null;
	};

	const getAutocompleteItemText = (customer: Customer) => {
		if (customer.name && customer.name.trim() !== "") {
			return customer.name;
		}

		return customer.PhoneNumbers?.[0]?.phoneNumber || customer.EmailAddresses?.[0]?.emailAddress || "[Contact]";
	};

	function shouldShowItemPhoneSubtitle(customer: Customer): boolean {
		if (!customer.phone?.trim()) return false;
		return getAutocompleteItemText(customer).trim() !== customer.phone.trim();
	}

	function shouldShowItemEmailSubtitle(customer: Customer): boolean {
		if (!customer.email?.trim()) return false;
		return getAutocompleteItemText(customer).trim() !== customer.email.trim();
	}

	const instance = getCurrentInstance();
	const customerAutocompleteRef = ref<BaseAutoCompleteComponent | null>(null);
	const customerAutocompleteActivatorRef = ref<HTMLElement | null>(null);
	const { menuProps } = useKeyboardSafeMenu(customerAutocompleteActivatorRef);

	function syncCustomerAutocompleteActivatorRef() {
		customerAutocompleteActivatorRef.value = customerAutocompleteRef.value?.getActivatorElement?.() ?? null;
	}

	onMounted(syncCustomerAutocompleteActivatorRef);
	onUpdated(syncCustomerAutocompleteActivatorRef);

	const { items, isLoading, searchTerm, onSearchInput, refresh } = useAsyncAutocompleteItems<Customer>({
		fetchItems: async (term: string) => {
			const results = await api.searchCustomers(term);
			return results.rows as Customer[];
		},
		mapItems: (arr) => getSearchLabelForCustomers(arr as any) as any,
		getSelected: () => (instance as any)?.proxy?.$attrs?.modelValue ?? (instance as any)?.proxy?.$attrs?.value
	});
	// Expose items under the original name used in template
	const customers = items as Ref<Customer[]>;

	const isMultiple = computed<boolean>(() => (instance as any)?.proxy?.$attrs?.multiple === true);
	const selected = computed<any>(() => (instance as any)?.proxy?.$attrs?.modelValue ?? (instance as any)?.proxy?.$attrs?.value);
	const isDisabled = computed<boolean>(() => !!(instance as any)?.proxy?.$attrs?.disabled);
	const hasSingleSelection = computed<boolean>(() => !isMultiple.value && !!selected.value && !!selected.value.id);

	const appendIcon = computed<string>(() => (hasSingleSelection.value ? "icon-pencil-edit-solid" : "icon-plus"));

	const tooltipText = computed<string>(() => (hasSingleSelection.value ? "Edit contact details." : "Create Contact"));

	const shouldShowCreateButton = computed<boolean>(() => {
		if (isDisabled.value) return false;
		const term = ((searchTerm as Ref<string>).value || "").trim();
		if (!term) return false;
		const lower = term.toLowerCase();
		return !(customers.value || []).filter(Boolean).some((c) => (getAutocompleteItemText(c) || "").trim().toLowerCase() === lower);
	});

	function getChipText(item: any) {
		if (item.raw) {
			return getAutocompleteItemText(item.raw);
		}
		return item.title || "";
	}

	async function handleChipEdit(customer: Customer) {
		const record: Customer | null = await (instance as any)?.proxy?.$root?.$createCustomerDialog?.editContact(customer.id);
		if (record) {
			updateItems(record);
			if (isMultiple.value) {
				const current = Array.isArray(selected.value) ? selected.value : selected.value ? [selected.value] : [];
				const updated = current.map((c: Customer) => (c.id === record.id ? record : c));
				(instance as any)?.proxy?.$emit("update:modelValue", updated);
			} else {
				(instance as any)?.proxy?.$emit("update:modelValue", record);
			}
		}
	}

	async function handleAppendClick() {
		if (isDisabled.value) return;
		if (hasSingleSelection.value) {
			const record: Customer | null = await (instance as any)?.proxy?.$root?.$createCustomerDialog?.editContact(selected.value.id);
			if (record) {
				updateItems(record);
				(instance as any)?.proxy?.$emit("update:modelValue", record);
			}
		} else {
			await openCreateDialog();
		}
	}

	async function handleCreateClick() {
		if (isDisabled.value) return;
		await openCreateDialog();
	}

	async function openCreateDialog() {
		const record: Customer | null = await (instance as any)?.proxy?.$root?.$createCustomerDialog?.open(
			(searchTerm as Ref<string>).value || ""
		);
		if (record) {
			updateItems(record);
			if (isMultiple.value) {
				const current = Array.isArray(selected.value) ? selected.value : selected.value ? [selected.value] : [];
				// Dialog can resolve with an existing customer (Use existing path) — dedupe.
				if (!current.some((c: Customer) => c.id === record.id)) {
					(instance as any)?.proxy?.$emit("update:modelValue", [...current, record]);
				}
			} else {
				(instance as any)?.proxy?.$emit("update:modelValue", record);
			}
		}
	}

	function updateItems(item: Customer) {
		try {
			// Find the index of the item with matching ID
			const existingIndex = items.value.findIndex((existingItem) => existingItem.id === item.id);

			if (existingIndex !== -1) {
				// Update the existing item by creating a new array
				items.value = [...items.value.slice(0, existingIndex), item, ...items.value.slice(existingIndex + 1)];
			} else {
				// Add the new item to the array
				items.value = [...items.value, item];
			}
		} catch (error) {
			console.error("Failed to update customer item:", error);
		}
	}
</script>

<style scoped>
	.create-contact-fixed {
		position: sticky !important;
		bottom: 0;
		background-color: white;
		z-index: 1;
	}

	.create-contact-btn {
		white-space: normal;
		overflow-wrap: break-word;
	}

	.create-contact-btn:hover {
		background-color: transparent !important;
	}

	.create-contact-btn:hover::before {
		opacity: 0 !important;
	}

	.create-contact-fixed:hover,
	.create-contact-fixed:hover::after {
		background-color: color-mix(in srgb, rgb(var(--v-theme-on-surface)) calc(var(--v-hover-opacity) * 100%), white);
	}
</style>
